// Rendering helpers for email bodies in the comms admin. Ingest stores `text ?? html` in
// `body_text`, so HTML-only emails hold raw markup; these decide when to render it (in a
// sandboxed iframe) and build the sanitized, locked-down document it renders.

const HTML_START = /^<(!doctype|html|head|body|meta|table|div|style|p)(?=[\s>/])/i
const CLOSING_TAG_NAME = /<\/([a-z][a-z0-9-]*)\s*>/gi
const OPENING_TAG_NAME = /<([a-z][a-z0-9-]*)(?=[\s>/])/gi
const VOID_TAG = /<(br|hr)\s*\/?>|<img\s[^>]*>/gi

function tagNames(body: string, re: RegExp): Set<string> {
  return new Set(Array.from(body.matchAll(re), (m) => m[1].toLowerCase()))
}

/**
 * Linear-time heuristic: a document start, a matched open/close tag pair (`<section>…</section>`,
 * but not a lone `</div>` mentioned in prose), or two or more void tags (`Hi<br>Thanks<br>Tait`).
 * Angle-bracketed addresses, quotes and URLs never match: `<jo@x.com>` / `<https://…>` are not
 * followed by whitespace, `>` or `/` right after a tag-like name.
 */
export function looksLikeHtml(body: string): boolean {
  const trimmed = body.trim()
  if (HTML_START.test(trimmed)) return true
  const closing = tagNames(trimmed, CLOSING_TAG_NAME)
  if (closing.size > 0) {
    for (const name of tagNames(trimmed, OPENING_TAG_NAME)) if (closing.has(name)) return true
  }
  return (trimmed.match(VOID_TAG) ?? []).length >= 2
}

// Elements that can load, navigate or run something. Forms are kept: `form-action 'none'`
// in the CSP stops them submitting.
const REMOVED_SELECTOR = [
  // Every sender <meta>: http-equiv refresh/CSP, `name=referrer` (would override our
  // no-referrer policy and leak this admin URL). Charset is moot: we parse a JS string.
  "meta",
  "link",
  "base",
  "script",
  "noscript",
  "iframe",
  "frame",
  "frameset",
  "object",
  "embed",
  "applet",
  // Template content is a separate DocumentFragment that querySelectorAll never reaches, and
  // `<template shadowrootmode>` re-activates it as a declarative shadow root on the srcdoc
  // parse, so anything inside would bypass this pass. Emails never need templates.
  "template",
].join(",")

const URL_ATTRIBUTES = ["href", "src", "action", "formaction", "xlink:href"]
const UNSAFE_URL = /^(javascript:|vbscript:|data:text\/html)/

function sanitize(doc: Document): void {
  doc.querySelectorAll(REMOVED_SELECTOR).forEach((el) => el.remove())
  doc.querySelectorAll("*").forEach((el) => {
    const tag = el.tagName.toLowerCase()
    if (tag === "a" || tag === "area") el.setAttribute("rel", "noopener noreferrer")
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase()
      if (name.startsWith("on") || name === "referrerpolicy") {
        el.removeAttribute(attr.name)
      } else if (URL_ATTRIBUTES.includes(name)) {
        // Browsers ignore leading whitespace/control chars in a URL scheme.
        // eslint-disable-next-line no-control-regex
        const value = attr.value.replace(/[\u0000- ]/g, "").toLowerCase()
        if (UNSAFE_URL.test(value)) el.removeAttribute(attr.name)
      }
    }
  })
}

/**
 * Parse with the browser's inert DOMParser (loads nothing, runs nothing), strip anything that
 * can navigate the frame, preload, or execute, then put our CSP / referrer / base / style first in <head>.
 * CSP and sandbox alone don't stop a `<meta http-equiv=refresh>` self-navigation or prefetch
 * hints, so those are removed here rather than relied on to be blocked.
 */
export function buildEmailSrcDoc(
  html: string,
  opts: { allowRemoteImages: boolean },
  parser: DOMParser = new DOMParser()
): string {
  const doc = parser.parseFromString(html, "text/html")
  sanitize(doc)

  const imgSrc = opts.allowRemoteImages ? "data: cid: https: http:" : "data: cid:"
  const csp = doc.createElement("meta")
  csp.setAttribute("http-equiv", "Content-Security-Policy")
  csp.setAttribute(
    "content",
    `default-src 'none'; style-src 'unsafe-inline'; img-src ${imgSrc}; font-src data:; form-action 'none'`
  )
  const referrer = doc.createElement("meta")
  referrer.setAttribute("name", "referrer")
  referrer.setAttribute("content", "no-referrer")
  const base = doc.createElement("base")
  base.setAttribute("target", "_blank")
  const style = doc.createElement("style")
  style.textContent =
    "html,body{background:#fff;color:#111;margin:0;padding:12px;font-family:system-ui,sans-serif;word-break:break-word}img{max-width:100%;height:auto}"

  // Reverse order so the CSP meta ends up first. (insertBefore, not prepend(): an ambient
  // Workers type shadows Element.prepend in this package's typecheck.)
  for (const el of [style, base, referrer, csp]) doc.head.insertBefore(el, doc.head.firstChild)
  return "<!DOCTYPE html>" + doc.documentElement.outerHTML
}
